// ============================================================================
// Practice games registry (spec §7.1). Each game renders a question for one
// (item, skill) and reports the result via onGrade({grade,isCorrect,responseMs}).
// The session runner (views/study.js) picks items, resolves content, and writes
// each result through record_review. Objective games auto-grade via the tested
// gradeFromObjective(); flashcards is self-graded.
// ============================================================================

import { el } from '../dom.js';
import { GAME, GRADE, SKILL, ITEM_TYPE } from '@core/models/constants.js';
import { gradeFromObjective } from '@core/srs/sm2.js';
import { token } from '../theme.js';
import { createHangulKeyboard } from '../keyboard/hangul-keyboard.js';
import {
  primaryReading, primaryEum, normalize, buildChoices, shuffle, speakText,
} from './shared.js';
import { speak } from '../audio.js';

const isChar = (item) => item.item_type === ITEM_TYPE.CHARACTER;

// --- Flashcards (mode 2, self-graded) --------------------------------------
function flashcards({ item, content, onGrade }) {
  let revealed = false;
  const wrap = el('div.stack');
  const front = isChar(item)
    ? el('div.flashcard__char', { text: content.char })
    : el('div.stack.center', {}, [
        el('div.flashcard__word', { text: content.hangul }),
        el('div.faint', { text: content.hanja_form || '' }),
      ]);
  const back = isChar(item)
    ? el('div.flashcard__answer', { text: primaryReading(content) || '—' })
    : el('div.flashcard__answer', { text: content.meaning_en || '—' });

  function paint() {
    const body = revealed
      ? el('div.flashcard', {}, [front, hr(), back])
      : el('div.flashcard', {}, [front]);
    const controls = revealed
      ? gradeButtons(onGrade)
      : el('button.btn.btn--primary.btn--block', { text: 'Show answer', onclick: () => { revealed = true; paint(); } });
    wrap.replaceChildren(body, controls);
  }
  paint();
  return wrap;
}

// --- Multiple choice (recognition, auto-graded) ----------------------------
function multipleChoice({ item, content, pool, onGrade }) {
  const started = Date.now();
  let answered = false;
  const correct = isChar(item) ? (primaryReading(content) || '—') : (content.meaning_en || '—');
  const distractPool = isChar(item)
    ? pool.chars.filter((c) => c.id !== content.id).map(primaryReading)
    : pool.words.filter((w) => w.id !== content.id).map((w) => w.meaning_en);
  const options = buildChoices(correct, distractPool, 4);

  const prompt = isChar(item)
    ? el('div.flashcard__char', { text: content.char })
    : el('div.stack.center', {}, [
        el('div.flashcard__word', { text: content.hangul }),
        el('div.faint', { text: content.hanja_form || '' }),
      ]);

  const opts = el('div.stack');
  options.forEach((o) => {
    const b = el('button.btn.btn--block', {
      text: o,
      onclick: () => {
        if (answered) return;
        answered = true;
        const ok = o === correct;
        [...opts.children].forEach((c) => {
          if (c.textContent === correct) c.classList.add('grade--good');
          else if (c === b && !ok) c.classList.add('grade--again');
          c.disabled = true;
        });
        setTimeout(() => onGrade({
          grade: gradeFromObjective(ok, Date.now() - started),
          isCorrect: ok,
          responseMs: Date.now() - started,
        }), ok ? 350 : 900);
      },
    });
    opts.append(b);
  });

  return el('div.stack', {}, [el('div.flashcard', {}, [prompt]), el('p.faint.center', { text: 'Pick the correct meaning / reading' }), opts]);
}

// --- Type the reading (production, on-screen keyboard) ----------------------
function typeReading({ item, content, onGrade }) {
  const started = Date.now();
  const answer = isChar(item) ? primaryEum(content) : content.hangul;
  const promptTop = isChar(item) ? content.char : (content.hanja_form || content.hangul);
  const promptSub = isChar(item) ? 'Type the reading (eum)' : 'Type the reading (hangul)';

  const input = el('input.input', { placeholder: '한글', autocomplete: 'off', autocapitalize: 'off', spellcheck: false, inputmode: 'text' });
  const kb = createHangulKeyboard({ input });

  const submit = el('button.btn.btn--primary.btn--block', {
    text: 'Check',
    onclick: () => {
      const ok = normalize(input.value) === normalize(answer);
      input.disabled = true;
      submit.replaceWith(feedback(ok, answer, onGrade, Date.now() - started));
    },
  });

  const form = el('form.stack', { onsubmit: (e) => { e.preventDefault(); submit.click(); } }, [
    el('div.flashcard', {}, [
      isChar(item) ? el('div.flashcard__char', { text: promptTop }) : el('div.flashcard__word', { text: promptTop }),
    ]),
    el('p.faint.center', { text: promptSub }),
    input,
    kb.el,
    submit,
  ]);
  setTimeout(() => input.focus(), 0);
  return form;
}

// --- Listening (recognition, audio) ----------------------------------------
function listening({ item, content, pool, onGrade }) {
  const started = Date.now();
  let answered = false;
  const say = () => speak(speakText(item, content));

  const correctDisplay = isChar(item) ? content.char : content.hangul;
  const distract = isChar(item)
    ? pool.chars.filter((c) => c.id !== content.id).map((c) => c.char)
    : pool.words.filter((w) => w.id !== content.id).map((w) => w.hangul);
  const options = buildChoices(correctDisplay, distract, 4);

  const opts = el('div.grid');
  options.forEach((o) => {
    const b = el('button.btn', {
      onclick: () => {
        if (answered) return; answered = true;
        const ok = o === correctDisplay;
        [...opts.children].forEach((c) => {
          if (c.dataset.v === correctDisplay) c.classList.add('grade--good');
          else if (c === b && !ok) c.classList.add('grade--again');
          c.disabled = true;
        });
        setTimeout(() => onGrade({ grade: gradeFromObjective(ok, Date.now() - started), isCorrect: ok, responseMs: Date.now() - started }), ok ? 350 : 900);
      },
      dataset: { v: o },
    }, [el('span', { class: isChar(item) ? 'flashcard__char' : 'flashcard__word', style: 'font-size:2.2rem', text: o })]);
    opts.append(b);
  });

  setTimeout(say, 250);
  return el('div.stack.center', {}, [
    el('p.faint', { text: 'Listen, then pick what you heard' }),
    el('button.btn.btn--primary', { text: '🔊 Play again', onclick: say }),
    opts,
  ]);
}

// --- Stroke-order writing (production, tracing canvas) ----------------------
function strokeWriting({ item, content, onGrade }) {
  // Only characters have stroke practice.
  const size = 260;
  const canvas = el('canvas', { width: size, height: size, style: `touch-action:none;border:1px solid var(--border);border-radius:var(--radius);background:var(--canvas-bg);width:${size}px;height:${size}px` });
  const ctx = canvas.getContext('2d');
  let drew = false, drawing = false;

  function guide() {
    ctx.clearRect(0, 0, size, size);
    ctx.save();
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = token('--canvas-ink') || '#000';
    ctx.font = `${size * 0.8}px ${token('--font-hanja') || 'serif'}`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(content.char, size / 2, size / 2 + 8);
    ctx.restore();
    ctx.strokeStyle = token('--canvas-ink') || '#000';
    ctx.lineWidth = 6; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  }
  const pos = (e) => { const r = canvas.getBoundingClientRect(); const p = e.touches?.[0] || e; return [p.clientX - r.left, p.clientY - r.top]; };
  const start = (e) => { drawing = true; drew = true; const [x, y] = pos(e); ctx.beginPath(); ctx.moveTo(x, y); e.preventDefault(); };
  const move = (e) => { if (!drawing) return; const [x, y] = pos(e); ctx.lineTo(x, y); ctx.stroke(); e.preventDefault(); };
  const end = () => { drawing = false; };
  canvas.addEventListener('pointerdown', start);
  canvas.addEventListener('pointermove', move);
  window.addEventListener('pointerup', end);
  guide();

  return el('div.stack.center', {}, [
    el('p.faint', { text: `Trace the character in stroke order: reading ${primaryReading(content)}` }),
    canvas,
    el('div.row', {}, [
      el('button.btn', { text: 'Clear', onclick: () => { drew = false; guide(); } }),
      el('button.btn.grade.grade--again', { text: "Couldn't", onclick: () => onGrade({ grade: GRADE.AGAIN, isCorrect: false }) }),
      el('button.btn.grade.grade--good', { text: 'Done', onclick: () => onGrade({ grade: drew ? GRADE.GOOD : GRADE.HARD, isCorrect: drew }) }),
    ]),
    el('p.faint', { text: 'v1 tracing (stroke-order validation arrives with stroke data — spec §20.2 #4).' }),
  ]);
}

// --- Fill in the gaps (production, needs a sentence) ------------------------
function fillBlank({ item, content, sentence, onGrade }) {
  if (!sentence) return needsSentences(onGrade);
  const started = Date.now();
  const answer = content.hangul;
  const blanked = sentence.text_ko.replace(answer, '____');
  const input = el('input.input', { placeholder: '한글', autocomplete: 'off' });
  const kb = createHangulKeyboard({ input });
  const submit = el('button.btn.btn--primary.btn--block', {
    text: 'Check',
    onclick: () => {
      const ok = normalize(input.value) === normalize(answer);
      input.disabled = true;
      submit.replaceWith(feedback(ok, answer, onGrade, Date.now() - started));
    },
  });
  return el('form.stack', { onsubmit: (e) => { e.preventDefault(); submit.click(); } }, [
    el('div.card', {}, [el('div.flashcard__answer', { text: blanked })]),
    sentence.translation_en ? el('p.faint.center', { text: sentence.translation_en }) : false,
    el('p.faint.center', { text: 'Fill the gap with the missing word' }),
    input, kb.el, submit,
  ]);
}

// --- Sentence construction (production, needs a sentence) -------------------
function sentenceConstruction({ sentence, onGrade }) {
  if (!sentence) return needsSentences(onGrade);
  const started = Date.now();
  const tokens = sentence.text_ko.split(/\s+/).filter(Boolean);
  const answer = tokens.join(' ');
  const chosen = [];
  const line = el('div.card', { style: 'min-height:56px' });
  const bank = el('div.row');

  function paint() {
    line.replaceChildren(el('div.flashcard__answer', { style: 'font-size:1.4rem', text: chosen.join(' ') || '…' }));
  }
  shuffle(tokens.map((t, i) => ({ t, i }))).forEach(({ t, i }) => {
    const tile = el('button.btn', { text: t, dataset: { i } , onclick: () => {
      chosen.push(t); tile.disabled = true; paint();
    }});
    bank.append(tile);
  });
  paint();

  return el('div.stack', {}, [
    sentence.translation_en ? el('p.faint.center', { text: sentence.translation_en }) : false,
    line, bank,
    el('div.row', {}, [
      el('button.btn', { text: 'Reset', onclick: () => { chosen.length = 0; [...bank.children].forEach((c) => (c.disabled = false)); paint(); } }),
      el('button.btn.btn--primary', { text: 'Check', onclick: () => {
        const ok = normalize(chosen.join(' ')) === normalize(answer);
        onGrade({ grade: gradeFromObjective(ok, Date.now() - started), isCorrect: ok, responseMs: Date.now() - started });
      }}),
    ]),
  ]);
}

// --- Word inference (recognition, derive meaning from parts, §10.1.2) -------
function wordInference({ item, content, pool, onGrade }) {
  const started = Date.now();
  let answered = false;
  const comps = (content.word_characters || [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((wc) => wc.characters)
    .filter(Boolean);
  const correct = content.meaning_en || '—';
  const distract = pool.words.filter((w) => w.id !== content.id).map((w) => w.meaning_en);
  const options = buildChoices(correct, distract, 4);

  const compRow = comps.length
    ? el('div.row.center', { style: 'gap:var(--space-3);flex-wrap:wrap;justify-content:center' },
        comps.map((c) => el('div.card.center', { style: 'padding:var(--space-3)' }, [
          el('div', { style: 'font-family:var(--font-hanja);font-size:2rem', text: c.char }),
          el('div.faint', { text: primaryEum(c) }),
          el('div.muted', { text: c.meaning_en || '' }),
        ])))
    : el('p.muted.center', { text: content.hanja_form || content.hangul });

  const opts = el('div.stack');
  options.forEach((o) => {
    const b = el('button.btn.btn--block', {
      text: o,
      onclick: () => {
        if (answered) return; answered = true;
        const ok = o === correct;
        [...opts.children].forEach((c) => {
          if (c.textContent === correct) c.classList.add('grade--good');
          else if (c === b && !ok) c.classList.add('grade--again');
          c.disabled = true;
        });
        setTimeout(() => onGrade({ grade: gradeFromObjective(ok, Date.now() - started), isCorrect: ok, responseMs: Date.now() - started }), ok ? 350 : 900);
      },
    });
    opts.append(b);
  });

  return el('div.stack', {}, [
    el('div.stack.center', {}, [
      el('div', { style: 'font-family:var(--font-hanja);font-size:2.4rem', text: content.hanja_form || content.hangul }),
      el('div.faint', { text: content.hangul }),
    ]),
    el('p.faint.center', { text: 'Infer the compound’s meaning from its parts' }),
    compRow,
    opts,
  ]);
}

// --- Confusable discrimination (recognition, homophone distractors, §10.1.3)-
function discrimination({ item, content, pool, onGrade }) {
  const started = Date.now();
  let answered = false;
  const eum = primaryEum(content);
  const sameEum = (pool.chars || []).filter((c) => c.id !== content.id && primaryEum(c) === eum);
  const distract = shuffle(sameEum).slice(0, 3).map((c) => c.char);
  if (distract.length < 3) {
    for (const c of shuffle(pool.chars || [])) {
      if (distract.length >= 3) break;
      if (c.char !== content.char && !distract.includes(c.char)) distract.push(c.char);
    }
  }
  const options = shuffle([content.char, ...distract]);
  const hint = sameEum.length
    ? `These all read “${eum}” — pick the one that means:`
    : 'Pick the character that means:';

  const opts = el('div.grid');
  options.forEach((o) => {
    const b = el('button.btn', {
      dataset: { v: o },
      onclick: () => {
        if (answered) return; answered = true;
        const ok = o === content.char;
        [...opts.children].forEach((c) => {
          if (c.dataset.v === content.char) c.classList.add('grade--good');
          else if (c === b && !ok) c.classList.add('grade--again');
          c.disabled = true;
        });
        setTimeout(() => onGrade({ grade: gradeFromObjective(ok, Date.now() - started), isCorrect: ok, responseMs: Date.now() - started }), ok ? 350 : 900);
      },
    }, [el('span.flashcard__char', { style: 'font-size:2.2rem', text: o })]);
    opts.append(b);
  });

  return el('div.stack.center', {}, [
    el('p.faint', { text: hint }),
    el('div.flashcard__answer', { text: content.meaning_en || '—' }),
    opts,
  ]);
}

// --- shared bits ------------------------------------------------------------
function hr() { return el('hr', { style: 'width:60%;border:none;border-top:1px solid var(--border)' }); }
function gradeButtons(onGrade) {
  return el('div.grades', {}, [
    el('button.btn.grade.grade--again', { text: 'Again', onclick: () => onGrade({ grade: GRADE.AGAIN, isCorrect: false }) }),
    el('button.btn.grade.grade--hard', { text: 'Hard', onclick: () => onGrade({ grade: GRADE.HARD, isCorrect: true }) }),
    el('button.btn.grade.grade--good', { text: 'Good', onclick: () => onGrade({ grade: GRADE.GOOD, isCorrect: true }) }),
    el('button.btn.grade.grade--easy', { text: 'Easy', onclick: () => onGrade({ grade: GRADE.EASY, isCorrect: true }) }),
  ]);
}
function feedback(ok, answer, onGrade, ms) {
  return el('div.stack', {}, [
    el('div.banner', { class: `banner ${ok ? 'banner--info' : 'banner--warn'}`, text: ok ? '✓ Correct' : `✗ Answer: ${answer}` }),
    el('button.btn.btn--primary.btn--block', { text: 'Continue', onclick: () => onGrade({ grade: gradeFromObjective(ok, ms), isCorrect: ok, responseMs: ms }) }),
  ]);
}
function needsSentences(onGrade) {
  return el('div.stack.center', {}, [
    el('div.banner.banner--warn', { text: 'No example sentence available for this item yet. Sentences are Claude-generated and approval-gated (spec §12.2).' }),
    el('button.btn', { text: 'Skip', onclick: () => onGrade({ grade: GRADE.GOOD, isCorrect: true, skip: true }) }),
  ]);
}

// --- Registry ---------------------------------------------------------------
export const GAMES = [
  { id: GAME.FLASHCARDS, label: 'Flashcards', skill: SKILL.RECOGNITION, appliesChar: true, appliesWord: true, needsSentence: false, build: flashcards },
  { id: GAME.MULTIPLE_CHOICE, label: 'Multiple choice', skill: SKILL.RECOGNITION, appliesChar: true, appliesWord: true, needsSentence: false, build: multipleChoice },
  { id: GAME.TYPE_READING, label: 'Type the reading', skill: SKILL.PRODUCTION, appliesChar: true, appliesWord: true, needsSentence: false, build: typeReading },
  { id: GAME.LISTENING, label: 'Listening', skill: SKILL.RECOGNITION, appliesChar: true, appliesWord: true, needsSentence: false, build: listening },
  { id: GAME.STROKE_WRITING, label: 'Stroke writing', skill: SKILL.PRODUCTION, appliesChar: true, appliesWord: false, needsSentence: false, build: strokeWriting },
  { id: GAME.FILL_BLANK, label: 'Fill in the gap', skill: SKILL.PRODUCTION, appliesChar: false, appliesWord: true, needsSentence: true, build: fillBlank },
  { id: GAME.SENTENCE_CONSTRUCTION, label: 'Sentence order', skill: SKILL.PRODUCTION, appliesChar: false, appliesWord: true, needsSentence: true, build: sentenceConstruction },
  { id: GAME.WORD_INFERENCE, label: 'Word inference', skill: SKILL.RECOGNITION, appliesChar: false, appliesWord: true, needsSentence: false, build: wordInference },
  { id: GAME.DISCRIMINATION, label: 'Tell apart (readings)', skill: SKILL.RECOGNITION, appliesChar: true, appliesWord: false, needsSentence: false, build: discrimination },
];

export function gameById(id) {
  return GAMES.find((g) => g.id === id);
}
